import React, { useState, useEffect, useRef } from 'react';
import { Server, Brain, Code, HardDrive, GitBranch, Zap, CheckCircle } from 'lucide-react';
import { portfolioData } from '../data/mock';

const sections = [
  { key: 'infrastructure', label: 'Infrastructure', icon: Server, color: 'from-cyan-500 to-teal-500' },
  { key: 'aiAutomation', label: 'AI & Automation', icon: Brain, color: 'from-violet-500 to-purple-500' },
  { key: 'backendSystems', label: 'Backend Systems', icon: Code, color: 'from-blue-500 to-indigo-500' },
  { key: 'infrastructureEngineering', label: 'Infrastructure Engineering', icon: HardDrive, color: 'from-emerald-500 to-green-500' },
  { key: 'apiIntegration', label: 'API & Integration', icon: GitBranch, color: 'from-amber-500 to-orange-500' }
];

const WorkImpact = () => {
  const [visible, setVisible] = useState(false);
  const [activeTab, setActiveTab] = useState('infrastructure');
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.disconnect(); } },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const activeSection = sections.find(s => s.key === activeTab);
  const activeItems = portfolioData.workImpact[activeTab] || [];

  return (
    <section id="work" ref={sectionRef} className="relative py-24 sm:py-32 overflow-hidden">
      <div className="absolute inset-0 grid-bg" />
      <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-indigo-500/5 rounded-full blur-[100px]" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`transition-all duration-1000 transform ${visible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
          <div className="text-center mb-16">
            <div className="inline-flex items-center space-x-2 px-3 py-1.5 rounded-full bg-indigo-500/10 border border-indigo-500/20 text-indigo-300 text-xs font-mono mb-4">
              <Zap size={12} />
              <span>Work Impact</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
              Real-World <span className="gradient-text">Outcomes</span>
            </h2>
            <p className="text-gray-500 max-w-2xl mx-auto">Engineering achievements across infrastructure, AI, backend, and platform domains</p>
          </div>

          <div className="max-w-5xl mx-auto">
            <div className="flex flex-wrap justify-center gap-2 mb-10">
              {sections.map((s) => {
                const Icon = s.icon;
                const isActive = activeTab === s.key;
                return (
                  <button key={s.key} onClick={() => setActiveTab(s.key)}
                    className={`flex items-center space-x-2 px-4 py-2.5 rounded-xl text-xs font-medium transition-all duration-300 ${
                      isActive ? 'bg-indigo-500/20 text-indigo-300 border border-indigo-500/30' : 'glass text-gray-400 hover:text-white hover:border-white/10'
                    }`}>
                    <Icon size={14} />
                    <span>{s.label}</span>
                  </button>
                );
              })}
            </div>

            <div className="glass rounded-3xl p-8 sm:p-10 border border-white/5">
              <div className="flex items-center space-x-3 mb-8">
                {activeSection && (
                  <>
                    <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${activeSection.color} flex items-center justify-center`}>
                      <activeSection.icon className="text-white" size={20} />
                    </div>
                    <h3 className="text-white font-semibold text-lg">{activeSection.label}</h3>
                  </>
                )}
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                {activeItems.map((item, i) => (
                  <div key={i} className="flex items-start space-x-3 p-4 rounded-xl bg-white/[0.02] hover:bg-white/[0.04] transition-all duration-300 group"
                    style={{
                      opacity: visible ? 1 : 0,
                      transform: visible ? 'translateY(0)' : 'translateY(10px)',
                      transitionDelay: `${i * 80}ms`,
                      transitionProperty: 'all',
                      transitionDuration: '0.5s',
                      transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)'
                    }}>
                    <CheckCircle size={16} className="text-indigo-400 mt-0.5 flex-shrink-0 group-hover:text-indigo-300 transition-colors" />
                    <span className="text-gray-300 text-sm leading-relaxed">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WorkImpact;
